import { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { useT, useLang } from "@/lib/i18n";
import { trackEvent } from "@/lib/analytics";

export default function Header() {
  const t = useT();
  const { lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const NAV = [
    { label: t.nav.services, href: "#services", key: "services" },
    { label: t.nav.process, href: "#process", key: "process" },
    { label: t.nav.contact, href: "#contact", key: "contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const LangToggle = ({ idPrefix = "lang" }) => (
    <div
      data-testid={`${idPrefix}-toggle`}
      className="inline-flex items-center text-[10px] tracking-[0.3em] uppercase font-semibold border border-[#2A2A2A] rounded-sm overflow-hidden"
    >
      <button
        data-testid={`${idPrefix}-toggle-en`}
        onClick={() => setLang("en")}
        className={`px-2.5 py-1.5 transition-colors ${
          lang === "en"
            ? "bg-[#D4AF37] text-[#0A0A0A]"
            : "text-[#A3A3A3] hover:text-[#F5F5F0]"
        }`}
        aria-label="Switch to English"
      >
        EN
      </button>
      <button
        data-testid={`${idPrefix}-toggle-hu`}
        onClick={() => setLang("hu")}
        className={`px-2.5 py-1.5 transition-colors ${
          lang === "hu"
            ? "bg-[#D4AF37] text-[#0A0A0A]"
            : "text-[#A3A3A3] hover:text-[#F5F5F0]"
        }`}
        aria-label="Magyarra váltás"
      >
        HU
      </button>
    </div>
  );

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 h-20 flex items-center justify-between">
        <a href="#top" data-testid="header-logo" className="flex items-center gap-3 group">
          <img
            src="/logo-icon-dark.png"
            alt="Videosbolt · Digit-Studio"
            className="h-10 md:h-12 w-auto"
            width="500"
            height="350"
          />
          <div className="flex flex-col leading-tight">
            <span className="font-serif-display text-xl md:text-2xl tracking-tight text-[#F5F5F0]">Videosbolt</span>
            <span className="text-[9px] tracking-[0.3em] uppercase text-[#A3A3A3] group-hover:text-[#D4AF37] transition">
              Digit-Studio
            </span>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((n) => (
            <a
              key={n.key}
              href={n.href}
              data-testid={`nav-${n.key}`}
              className="text-sm tracking-wide text-[#A3A3A3] hover:text-[#F5F5F0] transition-colors"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:block">
            <LangToggle />
          </div>
          <a
            href={BRAND.phoneHref}
            data-testid="header-call-button"
            onClick={() => trackEvent("call_click", { source: "header" })}
            className="hidden sm:inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#C49A26] text-[#0A0A0A] px-5 py-2.5 rounded-sm text-sm font-semibold tracking-wide transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-8px_rgba(212,175,55,0.6)]"
          >
            <Phone className="w-4 h-4" strokeWidth={2.2} />
            {t.nav.callNow}
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden p-2 text-[#F5F5F0]"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/10">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <a
                key={n.key}
                href={n.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.key}`}
                className="text-base text-[#A3A3A3] hover:text-[#F5F5F0]"
              >
                {n.label}
              </a>
            ))}
            <div className="flex items-center justify-between pt-2 border-t border-white/10">
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">
                {lang === "hu" ? "Nyelv" : "Language"}
              </span>
              <LangToggle idPrefix="mobile-lang" />
            </div>
            <a
              href={BRAND.phoneHref}
              data-testid="mobile-call-button"
              onClick={() => trackEvent("call_click", { source: "mobile_menu" })}
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A0A0A] px-5 py-3 rounded-sm font-semibold"
            >
              <Phone className="w-4 h-4" /> {BRAND.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
